import { AVATAR_MIME_TYPES, AVATAR_OUTPUT_SIZE, MAX_AVATAR_BYTES, MAX_QUESTION_IMAGE_BYTES, QUESTION_IMAGE_MAX_EDGE, QUESTION_IMAGE_MIME_TYPES } from "./shared";

const MAX_SOURCE_BYTES = 12 * 1024 * 1024;

export function validateAvatarSource(file: Pick<File, "type" | "size">): void {
  if (!(AVATAR_MIME_TYPES as readonly string[]).includes(file.type)) {
    throw new Error("JPEG・PNG・WebP形式の画像を選んでください");
  }
  if (file.size <= 0 || file.size > MAX_SOURCE_BYTES) {
    throw new Error("元画像は12MB以下にしてください");
  }
}

function canvasBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("画像を変換できませんでした")), "image/jpeg", quality);
  });
}

function blobDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("画像を読み込めませんでした"));
    reader.onerror = () => reject(new Error("画像を読み込めませんでした"));
    reader.readAsDataURL(blob);
  });
}

async function imageSource(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
  }
  const url = URL.createObjectURL(file);
  const image = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("画像を読み込めませんでした"));
      image.src = url;
    });
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

export async function prepareAvatar(file: File): Promise<string> {
  validateAvatarSource(file);
  const image = await imageSource(file);
  try {
    const side = Math.min(image.width, image.height);
    if (!Number.isFinite(side) || side <= 0) throw new Error("画像の大きさを確認できませんでした");
    const sourceX = (image.width - side) / 2;
    const sourceY = (image.height - side) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = AVATAR_OUTPUT_SIZE;
    canvas.height = AVATAR_OUTPUT_SIZE;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("画像を変換できませんでした");
    context.fillStyle = "#fffdf8";
    context.fillRect(0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);
    context.drawImage(image.source, sourceX, sourceY, side, side, 0, 0, AVATAR_OUTPUT_SIZE, AVATAR_OUTPUT_SIZE);

    let blob: Blob | null = null;
    for (const quality of [0.88, 0.78, 0.68, 0.58, 0.5]) {
      blob = await canvasBlob(canvas, quality);
      if (blob.size <= MAX_AVATAR_BYTES) break;
    }
    if (!blob || blob.size > MAX_AVATAR_BYTES) throw new Error("画像を150KB以下に変換できませんでした");
    return blobDataUrl(blob);
  } finally {
    image.close();
  }
}

export async function prepareQuestionImage(file: File): Promise<string> {
  if (!(QUESTION_IMAGE_MIME_TYPES as readonly string[]).includes(file.type)) throw new Error("JPEG・PNG・WebP形式の画像を選んでください");
  if (file.size <= 0 || file.size > MAX_SOURCE_BYTES) throw new Error("元画像は12MB以下にしてください");
  const image = await imageSource(file);
  try {
    if (!image.width || !image.height) throw new Error("画像の大きさを確認できませんでした");
    const scale = Math.min(1, QUESTION_IMAGE_MAX_EDGE / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) throw new Error("画像を変換できませんでした");
    context.fillStyle = "#fffdf8";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image.source, 0, 0, image.width, image.height, 0, 0, canvas.width, canvas.height);
    let blob: Blob | null = null;
    for (const quality of [0.84, 0.74, 0.64, 0.54, 0.44, 0.34]) {
      blob = await canvasBlob(canvas, quality);
      if (blob.size <= MAX_QUESTION_IMAGE_BYTES) break;
    }
    if (!blob || blob.size > MAX_QUESTION_IMAGE_BYTES) throw new Error("画像を700KB以下に変換できませんでした");
    return blobDataUrl(blob);
  } finally { image.close(); }
}
